import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createMockSupabase } from './mocks';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseDirectIp = import.meta.env.VITE_SUPABASE_DIRECT_IP;

// SECURITY: Service role key should NEVER be exposed to client-side code
// Service role keys bypass Row Level Security and should only be used server-side
// Removed supabaseServiceKey and useServiceRole for security
export const isDemoMode =
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  !supabaseUrl ||
  !supabaseAnonKey ||
  supabaseUrl === 'https://supabase.com' ||
  supabaseAnonKey === 'YOUR_SUPABASE_ANON_KEY';

// Connection status tracking
interface SupabaseStatus {
  isConnected: boolean;
  lastError: Error | null;
  lastChecked: Date | null;
  connectionAttempts: number;
}

export const supabaseStatus: SupabaseStatus = {
  isConnected: false,
  lastError: null,
  lastChecked: null,
  connectionAttempts: 0,
};

export let isOffline = false;

// Singleton instance to prevent multiple initializations
let supabaseInstance: SupabaseClient | null = null;

/**
 * Initialize the Supabase client with appropriate settings
 * Uses singleton pattern to prevent race conditions
 */
function initializeSupabase(): SupabaseClient {
  // Return existing instance if already initialized
  if (supabaseInstance) {
    return supabaseInstance;
  }
  // If in demo mode, use mock client
  if (isDemoMode) {
    if (import.meta.env.DEV) {
      console.warn(
        '⚠️ Running in DEMO mode with mock data. No real database connection.'
      );
    }
    supabaseInstance = createMockSupabase() as SupabaseClient;
    return supabaseInstance;
  }

  // Always use the anonymous key for client-side operations
  const key = supabaseAnonKey;

  // If we're using direct IP connection, modify the URL
  let url = supabaseUrl;
  if (supabaseDirectIp && import.meta.env.VITE_USE_DIRECT_IP === 'true') {
    const urlObj = new URL(supabaseUrl);
    // Replace hostname with direct IP but keep the path
    url = supabaseUrl.replace(urlObj.hostname, supabaseDirectIp);
    console.log(`Using direct IP connection: ${url}`);
  }

  // Use custom fetch to handle network issues more gracefully
  const options = {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
    },
    realtime: {
      params: {
        eventsPerSecond: 2,
      },
    },
    fetch: customFetch,
  };

  if (import.meta.env.DEV) {
    console.log('[Supabase] Initializing client with:', {
      url,
      isDemoMode,
      hasKey: !!key,
    });
  }
  supabaseInstance = createClient(url, key, options);
  if (import.meta.env.DEV) {
    console.log('[Supabase] Client initialized successfully');
  }
  return supabaseInstance;
}

/**
 * Custom fetch handler for Supabase to better handle network issues
 */
const customFetch = async (url: RequestInfo | URL, options?: RequestInit) => {
  // First check if we're offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    isOffline = true;
    console.warn('Device is offline. Cannot connect to Supabase.');
    throw new Error(
      'You are currently offline. Please check your network connection.'
    );
  }

  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    console.error('Error connecting to Supabase:', error);
    isOffline = true;

    // Enhance error message based on common connection issues
    let message = error instanceof Error ? error.message : String(error);

    if (
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('certificate')
    ) {
      message =
        'Cannot connect to the database. Please check your network connection.';
    }

    throw new Error(message);
  }
};

/**
 * Use direct IP connection for Supabase instead of domain
 * This can help when DNS or hostname resolution issues occur
 */
export function useDirectIpConnection() {
  if (!supabaseDirectIp) {
    console.warn('No direct IP configured, cannot use direct connection');
    return;
  }

  const urlObj = new URL(supabaseUrl);
  const directUrl = supabaseUrl.replace(urlObj.hostname, supabaseDirectIp);

  console.log(`Switching to direct IP connection: ${directUrl}`);

  // Return a new client instance with the direct IP
  return createClient(directUrl, supabaseAnonKey);
}

/**
 * Check if Supabase connection is working
 */
export async function checkSupabaseConnection(): Promise<boolean> {
  if (isDemoMode) {
    supabaseStatus.isConnected = true;
    supabaseStatus.lastChecked = new Date();
    return true;
  }

  try {
    supabaseStatus.connectionAttempts++;

    // Simple query to check if the database is responsive
    const { error } = await supabase
      .from('_health_check')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Supabase connection check failed:', error);
      supabaseStatus.isConnected = false;
      supabaseStatus.lastError = error;
      supabaseStatus.lastChecked = new Date();
      return false;
    }

    // If we get here, the connection is working
    supabaseStatus.isConnected = true;
    supabaseStatus.lastError = null;
    supabaseStatus.lastChecked = new Date();
    isOffline = false;
    return true;
  } catch (error) {
    console.error('Error checking Supabase connection:', error);
    supabaseStatus.isConnected = false;
    supabaseStatus.lastError =
      error instanceof Error ? error : new Error(String(error));
    supabaseStatus.lastChecked = new Date();
    return false;
  }
}

/**
 * Format Supabase errors into user-friendly messages
 */
export function handleSupabaseError(
  error: any,
  defaultMessage = 'An unexpected error occurred'
): string {
  if (!error) return defaultMessage;

  // If it's already a string, return it
  if (typeof error === 'string') return error;

  // If it's the standard Supabase error object
  if (error.message) return error.message;

  // Otherwise try to extract message from the error
  return (
    error.error_description ||
    error.errorMessage ||
    (error.details ? String(error.details) : defaultMessage)
  );
}

/**
 * Upload a file to Supabase Storage
 * @param file File to upload
 * @param path Optional custom path
 * @param folder Optional folder name
 * @returns Public URL of the uploaded file
 */
export async function uploadFile(
  file: File,
  path?: string | null,
  folder: string = ''
): Promise<string> {
  if (isDemoMode) {
    console.log('Demo mode: Simulating file upload');
    // In demo mode, just return a placeholder URL
    return `https://picsum.photos/seed/${Date.now()}/800/600`;
  }

  try {
    // Generate a unique filename
    const fileExt = file.name.split('.').pop();
    const uniqueId = Date.now().toString();
    const fileName = path || `${uniqueId}.${fileExt}`;

    // Add folder prefix if provided
    const filePath = folder ? `${folder}/${fileName}` : fileName;

    console.log(`Uploading file to path: ${filePath}`);

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      console.error('Error uploading file:', error);
      throw error;
    }

    console.log('File uploaded successfully:', data);

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from('images').getPublicUrl(data.path);

    return publicUrl;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
}

// Initialize the main Supabase client
export const supabase = initializeSupabase();

// Track active subscriptions for cleanup
const activeSubscriptions: Set<{ unsubscribe: () => void }> = new Set();

/**
 * Create a managed subscription that will be cleaned up automatically
 */
export function createManagedSubscription(channel: any): {
  unsubscribe: () => void;
} {
  const subscription = {
    unsubscribe: () => {
      channel.unsubscribe();
      activeSubscriptions.delete(subscription);
    },
  };
  activeSubscriptions.add(subscription);
  return subscription;
}

/**
 * Clean up all active subscriptions
 * Call this on app unmount or when switching users
 */
export function cleanupAllSubscriptions(): void {
  activeSubscriptions.forEach(sub => sub.unsubscribe());
  activeSubscriptions.clear();
}

/**
 * Reset the Supabase client instance
 * Useful for forcing reconnection after network issues
 */
export function resetSupabaseClient(): void {
  cleanupAllSubscriptions();
  supabaseInstance = null;
  // Re-initialize will happen on next access
}
